import {
  AgentInteractionResult,
  AgentType,
  APIChatHistoryMessage,
  AssistantConfig,
  APIChatMessage,
  DocumentReference,
  HistoricChat,
  InteractionContent,
  InteractionRequest,
  ToolCallActionCategory,
  ToolCallMessage,
  UiCommandInteractionContent,
  UiCommandInteractionRequest,
  UiCommandInteractionResult,
} from '@ardoq/api-types';
import type { AiThinkingStep } from '@ardoq/ai';
import type { AssistantDisplayMode } from './assistantStateOperations';
import type { AssistantBanner } from './assistantTypes';
import type { GeneratedTourDefinition } from './experimental/product-tour';
import { SendMessageRequest, type AgentInteractionRequested } from '@ardoq/api';

export type Feedback = 'good' | 'bad';

export type MessageBubblePosition = 'left' | 'right';

export type ChatMessageId = string;

export type AssistantCommands = {
  submitPositiveFeedback: ({
    messageId,
    agent,
  }: {
    messageId: ChatMessageId;
    agent?: AgentType;
  }) => void;
  submitNegativeFeedback: ({
    messageId,
    agent,
  }: {
    messageId: ChatMessageId;
    agent?: AgentType;
  }) => void;
  sendWithHistory: (payload: DecoratedSendMessageRequest) => void;
  cancel: VoidFunction;
  close: () => void;
  startNewChat: () => void;
  minimize: () => void;
  setDisplayMode: (mode: AssistantDisplayMode) => void;
  setPanelWidth: (width: number) => void;
  open: () => void;
  setInput: (value: string) => void;
  appendInput: (value: string) => void;
  setScrollTop: (value: number) => void;
  setShowScrollDownButton: (value: boolean) => void;
  submitFeedback: (payload: {
    messageId: ChatMessageId;
    feedback: string;
    agent?: AgentType;
  }) => void;
  dismissSendMessageError: VoidFunction;
  showHistory: () => void;
  hideHistory: () => void;
  openSnakeGame: VoidFunction;
  closeSnakeGame: VoidFunction;
  historicChatTogglePin: (chat: HistoricChat) => void;
  renameHistoricChat: (chat: HistoricChat, title: string) => void;
  deleteHistoricChat: (id: string) => void;
  setHistorySearchText: (term: string) => void;
  openHistoricChat: (chat: HistoricChat) => void;
  openFullscreenAndSend: (
    content: string,
    source: AssistantQuestionSource
  ) => void;
  openAndSend: (content: string, source: AssistantQuestionSource) => void;
  openAndPopulate: (content: string) => void;
  expandFullscreenWidth: () => void;
  shrinkFullscreenWidth: () => void;
  dismissDisplayModeHint: () => void;
  openMainNav: VoidFunction;
  navigateToManageAgents: VoidFunction;
  exportChatSession: (chatSessionId: string) => void;
  requestAssistantInputFocus: (requesting: boolean) => void;
  dismissBanner: (banner: AssistantBanner) => void;
  setAssistantConfig: (config: AssistantConfig) => void;
};

type WebSearchInfoContent =
  | { infoType: 'webSearchEnabled' }
  | { infoType: 'webSearchDisabled' };

type AgentFeedbackCard = {
  feedbackCardType: 'agent';
  agentName: string;
};

// expand this union type with other feedback card types as they're added
export type FeedbackCardProps = AgentFeedbackCard;

export type FeedbackCardContent = {
  infoType: 'feedbackCard';
} & FeedbackCardProps;

export type InfoContent = WebSearchInfoContent | FeedbackCardContent;

export type InteractionSubmissionState = 'submitting' | 'failed';

export type PlanTask = {
  id: string;
  kind: string;
  description: string;
  depends_on: string[];
};

export type PlanStatus =
  | 'proposed'
  | 'running'
  | 'replanning'
  | 'completed'
  | 'failed'
  | 'cancelled'
  | 'declined';

export type PlanStepStatus = 'pending' | 'running' | 'done' | 'skipped';

export type PlanContent = {
  goal: string;
  tasks: (PlanTask & { status: PlanStepStatus })[];
  status: PlanStatus;
};

type PlanChatMessage = {
  role: 'plan';
  content: PlanContent;
  exchangeId: string;
};

export type PlanOutcome = Extract<
  PlanStatus,
  'completed' | 'declined' | 'failed' | 'cancelled'
>;

export type PlanPayload = { goal: string; tasks: PlanTask[] };

export type PlanProposedEvent = {
  type: 'plan_proposed';
  event_id: string;
  session_id: string;
  plan: PlanPayload;
  loop_count: number;
};

export type TaskStartedEvent = {
  type: 'task_started';
  task_id: string;
  kind: string;
  description: string;
};

export type TaskFinishedEvent = {
  type: 'task_finished';
  task_id: string;
  outcome: { output: object } | { task_id: string; reason: string };
};

export type ExecutionFailedEvent = {
  type: 'execution_failed';
  event_id: string;
  session_id: string;
  results: object;
  loop_count: number;
};

export type OrchestrationConcludedEvent = {
  type: 'orchestration_concluded';
  event_id: string;
  session_id: string;
  outcome: PlanOutcome;
  plan: PlanPayload | null;
  reason: string | null;
};

export type AgentPlanEvent = { exchangeId: string } & (
  | PlanProposedEvent
  | TaskStartedEvent
  | TaskFinishedEvent
  | ExecutionFailedEvent
  | OrchestrationConcludedEvent
);

export type UserFacingInteractionContent = Exclude<
  InteractionContent,
  UiCommandInteractionContent
>;

type InteractionChatMessage = {
  role: 'interaction';
  content: UserFacingInteractionContent;
  exchangeId: string;
};

export type ChatMessage = (
  | APIChatMessage
  | { role: 'info'; content: InfoContent }
  | InteractionChatMessage
  | PlanChatMessage
) & {
  messageId: ChatMessageId;
  isReasoningVisible?: boolean;
  timestamp?: number;
  feedbackFormIsVisible?: boolean;
  additionalFeedbackInputValue?: string;
  additionalFeedbackInputIsVisible?: boolean;
  feedbackSuccessMessageIsVisible?: boolean;
  isCancelled?: boolean;
  /**
   * Transitional: the agent that produced or should continue this message.
   * Storing a hardcoded agent identifier on the client is expected to be
   * deprecated once agent routing moves server-side and the LLM orchestrates
   * custom agents; the UI would then no longer track a fixed set of agents.
   * Kept for the current early-stage integrations — avoid building on it beyond
   * present needs. See `getAgentType`.
   */
  agentType?: AgentType;
  // this is likely what will replace agentType but is outside the scope of this ticket, todo
  agentName?: string;
  tour?: GeneratedTourDefinition;
  isAnswerFinal?: boolean;
  thinkingSteps?: AiThinkingStep[];
  errorCode?: string;
};

type DistributiveOmit<T, K extends PropertyKey> = T extends unknown
  ? Omit<T, K>
  : never;

export type NewChatMessage = DistributiveOmit<ChatMessage, 'messageId'>;

export type UserChatMessage = Extract<ChatMessage, { role: 'user' }>;
export type AssistantChatMessage = Extract<ChatMessage, { role: 'assistant' }>;
export type DocumentChatMessage = Extract<ChatMessage, { role: 'document' }>;
export type InfoChatMessage = Extract<ChatMessage, { role: 'info' }>;
export type InteractionMessage = Extract<ChatMessage, { role: 'interaction' }>;
export type PlanMessage = Extract<ChatMessage, { role: 'plan' }>;

export type UserFacingInteractionRequest = Exclude<
  InteractionRequest,
  UiCommandInteractionRequest
>;

export type UserFacingInteraction = AgentInteractionRequested & {
  request: UserFacingInteractionRequest;
};

export type UserFacingInteractionResult = Exclude<
  AgentInteractionResult,
  UiCommandInteractionResult
>;

export const isUserChatMessage = (
  message: ChatMessage
): message is UserChatMessage => message.role === 'user';

export const isAssistantChatMessage = (
  message: ChatMessage
): message is AssistantChatMessage => message.role === 'assistant';

export const isDocumentChatMessage = (
  message: ChatMessage
): message is DocumentChatMessage => message.role === 'document';

export const isInfoChatMessage = (
  message: ChatMessage
): message is InfoChatMessage => message.role === 'info';

export const isInteractionMessage = (
  message: ChatMessage
): message is InteractionMessage => message.role === 'interaction';

export const isPlanMessage = (message: ChatMessage): message is PlanMessage =>
  message.role === 'plan';

export const getActivePlan = (
  messages: ChatMessage[],
  currentActiveRequest: string | null
): PlanMessage | null => {
  if (currentActiveRequest === null) return null;

  return (
    messages.findLast(
      (message): message is PlanMessage =>
        isPlanMessage(message) && message.exchangeId === currentActiveRequest
    ) ?? null
  );
};

const isPendingInteraction = (
  message: ChatMessage
): message is InteractionMessage =>
  isInteractionMessage(message) && message.content.status === 'pending';

export const getPendingInteractionExchangeId = (
  messages: ChatMessage[]
): string | null => messages.findLast(isPendingInteraction)?.exchangeId ?? null;

export const hasActivePendingInteraction = (
  pendingInteractionExchangeId: string | null,
  currentActiveRequest: string | null
): boolean =>
  currentActiveRequest !== null &&
  pendingInteractionExchangeId === currentActiveRequest;

type FeedbackCardChatMessage = InfoChatMessage & {
  content: FeedbackCardContent;
};

export const isFeedbackCardChatMessage = (
  message: ChatMessage
): message is FeedbackCardChatMessage =>
  isInfoChatMessage(message) && message.content.infoType === 'feedbackCard';

export const isToolCallMessage = (
  message: APIChatHistoryMessage
): message is ToolCallMessage & { timestamp: string; exchangeId: string } =>
  message.role === 'tool_call';

export const isDialogueChatMessage = (
  message: ChatMessage
): message is UserChatMessage | AssistantChatMessage =>
  isUserChatMessage(message) || isAssistantChatMessage(message);

export const isEmptyTextMessage = (message: ChatMessage): boolean =>
  isDialogueChatMessage(message) && !message.content.trim();

export type DecoratedSendMessageRequest = Omit<
  SendMessageRequest,
  'documents'
> & {
  timestamp: number;
  documents?: DocumentReference[];
  webSearchEnabled?: boolean;
  source: AssistantQuestionSource;
};

export type AssistantQuestionSource =
  | 'chatInput'
  | 'chatSuggestion'
  | 'searchModal'
  | 'viewPromptBar'
  | 'integrationsAutomap';

type ChatTraceMetadata = {
  agent_name: string;
  is_sub_agent: boolean;
  agent_run_id: string;
  parent_agent_run_id: string | null;
};

export type ChatMessageToolCallEvent = ChatTraceMetadata & {
  tool_call_id: string;
  action_description?: string;
  name?: string;
  action_category?: ToolCallActionCategory;
  input?: string | Record<string, unknown>;
};

/**
 * A tool finished and produced a structured result. `output` is the tool's
 * return value serialized as a JSON string (e.g. the Assistant Diagram
 * `generateDiagram` recipe). Forwarded by ardoq-api from the Python agent's
 * `tool_result` event.
 */
export type ChatMessageToolResultEvent = ChatTraceMetadata & {
  name?: string;
  output?: string;
  tool_call_id?: string;
  metadata?: unknown;
};

export type ChatMessageContentPayload = {
  answer?: string;
  reasoning?: string;
  tour?: GeneratedTourDefinition;
};

export type ChatMessageContentEvent = ChatTraceMetadata & {
  message: string;
};

export type ChatMessageDoneEvent = {
  cancelled?: boolean;
};

export type ChatMessageErrorEvent = {
  message: string;
  code?: string;
};

export type ChatSessionTitleGeneratedEvent = {
  chatSession: HistoricChat;
};

export type ChatSessionConcludedEvent = {
  chatSessionId: string;
  exchangeId: string;
  unread: boolean;
};

export type ChatSessionNotificationReadEvent = {
  chatSessionId: string;
};

/**
 * Stable English identifiers for built-in history bucket types. Variable
 * buckets (e.g. month-year groups for older chats) use the formatted month
 * string itself as the key.
 */
export const PINNED_BUCKET_KEY = 'Pinned';
export const TODAY_BUCKET_KEY = 'Today';
export const YESTERDAY_BUCKET_KEY = 'Yesterday';
export const LAST_7_DAYS_BUCKET_KEY = 'Last 7 days';
export const LAST_30_DAYS_BUCKET_KEY = 'Last 30 days';

export type HistoryBuckets = {
  [key: string]: HistoricChat[];
};
